import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const ORANGE = '#F18A00';
const BLACK  = '#161616';

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────
function ymOf(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}
function monthLabel(ym, locale = 'fr') {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });
}
function monthShort(ym, locale = 'fr') {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', year: 'numeric' });
}
const fmtEur = v => (v != null && v !== '')
  ? new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(v)) + ' €'
  : '—';

function generatePeriods(dateEffet, frequency, previsionnel) {
  if (!dateEffet || !frequency) return [];
  const monthsPerPeriod = frequency === 'semestrielle' ? 6 : 3;
  const totalMonths = 12;
  const nbPeriods = Math.floor(totalMonths / monthsPerPeriod);
  const start = new Date(dateEffet);
  const periods = [];
  for (let p = 0; p < nbPeriods; p++) {
    const lines = [];
    for (let m = 0; m < monthsPerPeriod; m++) {
      const monthIdx = p * monthsPerPeriod + m;
      const d = new Date(start.getFullYear(), start.getMonth() + monthIdx, 1);
      const ym = ymOf(d);
      const prev = (previsionnel || []).find(r => r.periode === ym);
      lines.push({ month: ym, previsionnel: parseFloat(prev?.montant) || 0, realise: null });
    }
    const declD = new Date(start.getFullYear(), start.getMonth() + (p + 1) * monthsPerPeriod, 1);
    periods.push({
      id: `p${p + 1}`,
      start_month: lines[0].month,
      end_month: lines[lines.length - 1].month,
      declaration_month: ymOf(declD),
      lines,
      declared_at: null,
      validated_at: null,
    });
  }
  return periods;
}

function computeStatus(period) {
  if (period.validated_at) return 'validee';
  if (period.declared_at) return 'declaree';
  const now = new Date();
  const [y, m] = period.declaration_month.split('-').map(Number);
  const declD = new Date(y, m - 1, 1);
  if (now < declD) return 'a_venir';
  return 'a_declarer';
}

const STATUS_STYLE = {
  a_venir:    { bg: '#F1F1EE', color: '#5F5E5A', dot: '#888780' },
  a_declarer: { bg: '#FAEEDA', color: '#854F0B', dot: '#EF9F27' },
  declaree:   { bg: '#E6F1FB', color: '#185FA5', dot: '#378ADD' },
  validee:    { bg: '#E1F5EE', color: '#0F6E56', dot: '#1D9E75' },
};

// ── Composant principal ───────────────────────────────────────
export default function SurveillanceRisque({ police, collecteGF, surveillance, onChange, onCommit, readOnly }) {
  const { t, lang } = useLanguage();
  const { isClient, isCourtier, isArcus } = useAuth();

  const isSouscrit = police?.statut === 'souscrit';
  const dateEffet = police?.date_effet;
  const previsionnel = collecteGF?.previsionnel || [];

  const data = surveillance || {};
  const frequency = data.frequency || null;
  const periods = data.periods || [];

  const [confirmReset, setConfirmReset] = useState(false);

  // Génération auto des périodes : si fréquence + souscrit + date d'effet + pas de périodes
  useMemo(() => {
    if (frequency && isSouscrit && dateEffet && periods.length === 0) {
      const newPeriods = generatePeriods(dateEffet, frequency, previsionnel);
      if (newPeriods.length) onChange({ ...data, frequency, periods: newPeriods });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, isSouscrit, dateEffet]);

  function setFrequency(v) {
    if (periods.length > 0 && v !== frequency) {
      setConfirmReset(true);
      return;
    }
    const newPeriods = isSouscrit ? generatePeriods(dateEffet, v, previsionnel) : [];
    onChange({ ...data, frequency: v, periods: newPeriods });
  }

  function confirmFreqChange(newFreq) {
    const newPeriods = isSouscrit ? generatePeriods(dateEffet, newFreq, previsionnel) : [];
    onChange({ ...data, frequency: newFreq, periods: newPeriods });
    setConfirmReset(false);
  }

  function updateLine(periodId, lineIdx, value) {
    if (readOnly && !isClient && !isCourtier) return;
    const newPeriods = periods.map(p => {
      if (p.id !== periodId) return p;
      const newLines = p.lines.map((l, i) => i === lineIdx ? { ...l, realise: value === '' ? null : parseFloat(value) } : l);
      return { ...p, lines: newLines };
    });
    onChange({ ...data, periods: newPeriods });
  }

  // ── Déclaration client : confirmation puis PERSISTANCE immédiate ──
  // (le Save n'est plus nécessaire : onCommit sauvegarde la police entière,
  //  chiffres saisis inclus, au moment de la confirmation).
  const [confirmDeclare, setConfirmDeclare] = useState(null); // période à confirmer
  const [confirmArcus, setConfirmArcus] = useState(false);    // modale en mode « déclarer et valider » (admin)
  const [committing, setCommitting] = useState(false);

  async function declarerPeriode(periodId) {
    const newPeriods = periods.map(p => p.id === periodId ? { ...p, declared_at: new Date().toISOString() } : p);
    const newData = { ...data, periods: newPeriods };
    setCommitting(true);
    try { await (onCommit ? onCommit(newData) : onChange(newData)); setConfirmDeclare(null); }
    finally { setCommitting(false); }
  }

  // Arcus déclare AU NOM du client : declared_at + validated_at posés ensemble,
  // en un seul commit — côté client, la période apparaît déclarée et validée.
  async function declarerEtValiderPeriode(periodId) {
    const now = new Date().toISOString();
    const newPeriods = periods.map(p => p.id === periodId ? { ...p, declared_at: now, validated_at: now } : p);
    const newData = { ...data, periods: newPeriods };
    setCommitting(true);
    try { await (onCommit ? onCommit(newData) : onChange(newData)); setConfirmDeclare(null); setConfirmArcus(false); }
    finally { setCommitting(false); }
  }

  // Validation Arcus : persistance immédiate également (alimente le Payment Graph).
  async function validerPeriode(periodId) {
    const newPeriods = periods.map(p => p.id === periodId ? { ...p, validated_at: new Date().toISOString() } : p);
    const newData = { ...data, periods: newPeriods };
    setCommitting(true);
    try { await (onCommit ? onCommit(newData) : onChange(newData)); }
    finally { setCommitting(false); }
  }

  // ── État : pas encore configuré ─────────────────────────────
  return (
    <div>
      {/* Introduction */}
      <div style={{ background:'#F7F7F5', border:'0.5px solid #E8E8E5', borderRadius:10, padding:'14px 16px', marginBottom:16, display:'flex', gap:11 }}>
        <SvgIcon size={20} color={ORANGE}>
          <path d="M12 9v4M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18M12 16h.01"/>
        </SvgIcon>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'#1A1A1A', marginBottom:3 }}>
            {t('gf.surveillance_intro_title')}
          </div>
          <div style={{ fontSize:12, color:'#64748B', lineHeight:1.5 }}>
            {t('gf.surveillance_intro_text')}
          </div>
        </div>
      </div>

      {/* Sélecteur de fréquence */}
      <div style={{ background:'#fff', border:'0.5px solid #E8E8E5', borderRadius:10, padding:16, marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:500, color:'#1A1A1A', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
          <SvgIcon size={17} color={ORANGE}><path d="M12 8l0 4l2 2M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9"/></SvgIcon>
          {t('gf.surveillance_frequency_label')}
        </div>
        {isArcus && !readOnly ? (
          <div style={{ display:'flex', gap:8 }}>
            {[
              { v:'trimestrielle', l:t('gf.surveillance_freq_trim') },
              { v:'semestrielle',  l:t('gf.surveillance_freq_sem')  },
            ].map(o => (
              <button key={o.v} onClick={() => setFrequency(o.v)}
                style={{ flex:1, padding:'10px', borderRadius:8, fontSize:14, cursor:'pointer',
                  fontWeight: frequency===o.v ? 500 : 400,
                  background: frequency===o.v ? BLACK : '#fff',
                  color: frequency===o.v ? '#fff' : '#64748B',
                  border: frequency===o.v ? 'none' : '1px solid #D9D9D5',
                  transition:'all .12s' }}>
                {o.l}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize:14, color:'#1A1A1A', fontWeight:500 }}>
            {frequency === 'trimestrielle' ? t('gf.surveillance_freq_trim')
              : frequency === 'semestrielle' ? t('gf.surveillance_freq_sem')
              : <span style={{ color:'#94A3B8', fontWeight:400 }}>{t('gf.surveillance_freq_not_set')}</span>}
          </div>
        )}
      </div>

      {/* État : ni fréquence ni souscription */}
      {!frequency && (
        <div style={{ background:'#FFF7F2', border:'1px solid #FED7AA', borderRadius:10, padding:'14px 16px', fontSize:13, color:'#9A5A00', display:'flex', alignItems:'center', gap:9 }}>
          <SvgIcon size={18} color="#9A5A00"><path d="M12 9v4M10.36 3.59l-8.1 13.53a1.9 1.9 0 0 0 1.63 2.88h16.22a1.9 1.9 0 0 0 1.63 -2.87l-8.1 -13.54a1.9 1.9 0 0 0 -3.27 0zM12 16h.01"/></SvgIcon>
          {t('gf.surveillance_pick_freq')}
        </div>
      )}
      {frequency && !isSouscrit && (
        <div style={{ background:'#E6F1FB', border:'1px solid #B5D4F4', borderRadius:10, padding:'14px 16px', fontSize:13, color:'#185FA5', display:'flex', alignItems:'center', gap:9 }}>
          <SvgIcon size={18} color="#185FA5"><path d="M12 8h.01M11 12h1v4h1M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18"/></SvgIcon>
          {t('gf.surveillance_not_subscribed')}
        </div>
      )}

      {/* Périodes générées */}
      {periods.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {periods.map((p, idx) => {
            const status = computeStatus(p);
            const ss = STATUS_STYLE[status];
            const isLocked = !!(p.declared_at || p.validated_at);
            // Toutes les périodes non verrouillées sont éditables (le client/courtier peut
            // anticiper les mois à venir). Seule la période échue peut être « déclarée »,
            // ce qui verrouille définitivement ses cases.
            const canEdit = !isLocked && (isClient || isCourtier || isArcus) && !readOnly;
            const isAnticipation = canEdit && status === 'a_venir';
            const canDeclare = !isLocked && (isClient || isCourtier)
              && p.lines.every(l => l.realise != null && l.realise !== '');
            // L'admin Arcus peut déclarer AU NOM du client (déclaré + validé en un geste).
            const canDeclareArcus = !isLocked && isArcus && !readOnly
              && p.lines.every(l => l.realise != null && l.realise !== '');
            const canValidate = status === 'declaree' && isArcus;
            const totalPrev = p.lines.reduce((s, l) => s + (parseFloat(l.previsionnel) || 0), 0);
            const totalReal = p.lines.reduce((s, l) => s + (parseFloat(l.realise) || 0), 0);
            const allReal = p.lines.every(l => l.realise != null);

            return (
              <div key={p.id} style={{ background:'#fff', border:'0.5px solid #E8E8E5', borderRadius:10, overflow:'hidden' }}>
                {/* Header période */}
                <div style={{ padding:'13px 16px', borderBottom:'0.5px solid #F1F1EE', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:500, color:'#1A1A1A' }}>
                      {t('gf.surveillance_period')} {idx + 1} · {monthLabel(p.start_month, lang)} → {monthLabel(p.end_month, lang)}
                    </div>
                    <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                      {t('gf.surveillance_declaration_in')} {monthLabel(p.declaration_month, lang)}
                    </div>
                    {isAnticipation && (
                      <div style={{ fontSize:11, color:'#854F0B', marginTop:3, display:'flex', alignItems:'center', gap:5 }}>
                        <SvgIcon size={12} color="#854F0B"><path d="M12 9v4M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18M12 16h.01"/></SvgIcon>
                        {t('gf.surveillance_anticipation_hint')}
                      </div>
                    )}
                  </div>
                  <span style={{ background:ss.bg, color:ss.color, padding:'4px 11px', borderRadius:99, fontSize:11, fontWeight:500, display:'inline-flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:ss.dot }}/>
                    {t('gf.surveillance_status_' + status)}
                  </span>
                </div>

                {/* Tableau */}
                <div style={{ padding:'4px 16px 12px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr 1fr 1fr', gap:'0 12px', padding:'10px 0', borderBottom:'0.5px solid #F1F1EE', fontSize:11, fontWeight:500, color:'#64748B', textTransform:'uppercase', letterSpacing:'.04em' }}>
                    <div>{t('gf.surveillance_col_month')}</div>
                    <div style={{ textAlign:'right' }}>{t('gf.surveillance_col_previsionnel')}</div>
                    <div style={{ textAlign:'right' }}>{t('gf.surveillance_col_realise')}</div>
                    <div style={{ textAlign:'right' }}>{t('gf.surveillance_col_ecart')}</div>
                  </div>
                  {p.lines.map((l, i) => {
                    const ecart = l.realise != null ? (parseFloat(l.realise) - parseFloat(l.previsionnel)) : null;
                    return (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr 1fr 1fr', gap:'0 12px', padding:'10px 0', borderBottom: i < p.lines.length-1 ? '0.5px solid #F7F7F5' : 'none', fontSize:13, alignItems:'center' }}>
                        <div style={{ color:'#1A1A1A', textTransform:'capitalize' }}>{monthShort(l.month, lang)}</div>
                        <div style={{ textAlign:'right', color:'#64748B' }}>{fmtEur(l.previsionnel)}</div>
                        <div style={{ textAlign:'right' }}>
                          {canEdit ? (
                            <input type="number" step="0.01" value={l.realise ?? ''}
                              onChange={e => updateLine(p.id, i, e.target.value)}
                              placeholder="0.00"
                              style={{ width:'100%', textAlign:'right', border:'1px solid #D9D9D5', borderRadius:6, padding:'6px 8px', fontSize:13, fontFamily:'inherit', outline:'none' }}/>
                          ) : (
                            <span style={{ color: l.realise != null ? '#1A1A1A' : '#CBD5E1', fontWeight: l.realise != null ? 500 : 400 }}>
                              {fmtEur(l.realise)}
                            </span>
                          )}
                        </div>
                        <div style={{ textAlign:'right', fontWeight:500,
                          color: ecart == null ? '#CBD5E1' : ecart >= 0 ? '#0F6E56' : '#A32D2D' }}>
                          {ecart == null ? '—' : (ecart >= 0 ? '+' : '') + fmtEur(ecart)}
                        </div>
                      </div>
                    );
                  })}
                  {/* Total ligne */}
                  {allReal && (
                    <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr 1fr 1fr', gap:'0 12px', padding:'10px 0', borderTop:'0.5px solid #E8E8E5', fontSize:13, fontWeight:500 }}>
                      <div style={{ color:'#64748B' }}>{t('gf.surveillance_total')}</div>
                      <div style={{ textAlign:'right', color:'#64748B' }}>{fmtEur(totalPrev)}</div>
                      <div style={{ textAlign:'right', color:'#1A1A1A' }}>{fmtEur(totalReal)}</div>
                      <div style={{ textAlign:'right', color: (totalReal - totalPrev) >= 0 ? '#0F6E56' : '#A32D2D' }}>
                        {(totalReal - totalPrev) >= 0 ? '+' : ''}{fmtEur(totalReal - totalPrev)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                {(canDeclare || canDeclareArcus || canValidate || status === 'declaree' || status === 'validee') && (
                  <div style={{ padding:'10px 16px', borderTop:'0.5px solid #F1F1EE', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                    <div style={{ fontSize:11, color:'#64748B' }}>
                      {p.declared_at && <span>{t('gf.surveillance_declared_at')} {new Date(p.declared_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                      {p.validated_at && <span style={{ marginLeft:12 }}>· {t('gf.surveillance_validated_at')} {new Date(p.validated_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      {canDeclare && (
                        <button onClick={() => { setConfirmArcus(false); setConfirmDeclare(p); }}
                          style={{ background:ORANGE, color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7 }}>
                          <SvgIcon size={15}><path d="M3 7l9 6l9 -6M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2"/></SvgIcon>
                          {t('gf.surveillance_declare_btn')}
                        </button>
                      )}
                      {canDeclareArcus && (
                        <button onClick={() => { setConfirmArcus(true); setConfirmDeclare(p); }}
                          style={{ background:'#0F6E56', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7 }}>
                          <SvgIcon size={15}><path d="M3 7l9 6l9 -6M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2"/></SvgIcon>
                          {t('gf.surveillance_declare_validate_btn')}
                        </button>
                      )}
                      {canValidate && (
                        <button onClick={() => validerPeriode(p.id)}
                          style={{ background:'#0F6E56', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7 }}>
                          <SvgIcon size={15}><path d="M5 12l5 5l10 -10"/></SvgIcon>
                          {t('gf.surveillance_validate_btn')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal confirmation changement fréquence */}
      {confirmReset && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:440, maxWidth:'92vw' }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:8 }}>{t('gf.surveillance_reset_title')}</div>
            <div style={{ fontSize:13, color:'#64748B', marginBottom:18 }}>{t('gf.surveillance_reset_warning')}</div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setConfirmReset(false)}
                style={{ fontSize:14, padding:'7px 16px', borderRadius:8, border:'1px solid #D9D9D5', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={() => confirmFreqChange(frequency === 'trimestrielle' ? 'semestrielle' : 'trimestrielle')}
                style={{ fontSize:14, padding:'7px 16px', borderRadius:8, background:'#A32D2D', color:'#fff', border:'none', cursor:'pointer', fontWeight:500 }}>
                {t('gf.surveillance_reset_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale — confirmation de déclaration (client/courtier) */}
      {confirmDeclare && (
        <div onClick={() => !committing && setConfirmDeclare(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:12, padding:24, width:470, maxWidth:'92vw' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:8, color:'#1A1A1A' }}>{t('gf.surveillance_declare_confirm_title')}</div>
            <div style={{ fontSize:13.5, color:'#475569', lineHeight:1.6, marginBottom:14 }}>{confirmArcus ? t('gf.surveillance_declare_validate_desc') : t('gf.surveillance_declare_confirm_desc')}</div>
            <div style={{ background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:8, padding:'10px 12px', marginBottom:16 }}>
              {(confirmDeclare.lines || []).map((l, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12.5, color:'#334155', padding:'2px 0' }}>
                  <span>{monthLabel(l.month, lang)}</span>
                  <strong>{l.realise != null && l.realise !== '' ? fmtEur(l.realise) : '\u2014'}</strong>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setConfirmDeclare(null)} disabled={committing}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E2E8F0', background:'#fff', cursor:'pointer' }}>{t('common.cancel')}</button>
              <button onClick={() => (confirmArcus ? declarerEtValiderPeriode(confirmDeclare.id) : declarerPeriode(confirmDeclare.id))} disabled={committing}
                style={{ padding:'8px 18px', borderRadius:8, border:'none', background:confirmArcus?'#0F6E56':ORANGE, color:'#fff', fontWeight:700, cursor:committing?'not-allowed':'pointer', opacity:committing?.6:1 }}>
                {committing ? '\u2026' : (confirmArcus ? t('gf.surveillance_declare_validate_btn') : t('gf.surveillance_declare_confirm_btn'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
